import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { X } from 'lucide-react';

const MASCOT = '/operio-mascot-3d.png';

/**
 * Per-feature first-visit tip. Shows once per (user, tipId).
 * Drop into any page:
 *   <FeatureTip tipId="projects" title="Dina projekt" body="..." />
 * After dismiss it never appears again for that user.
 *
 * If onboarding is still showing (not done), tips wait until next visit.
 */
const FeatureTip = ({ tipId, title, body, position = 'top-right' }) => {
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);
  const [closing, setClosing] = useState(false);

  const storageKey = user ? `operio-tip-${user.id}-${tipId}` : null;
  const onboardingKey = user ? `operio-onboarding-done-${user.id}` : null;

  useEffect(() => {
    if (!user || !storageKey || !onboardingKey) return;
    if (localStorage.getItem(storageKey)) return;
    if (!localStorage.getItem(onboardingKey)) return; // wait until onboarding finished
    const t = setTimeout(() => setVisible(true), 600);
    return () => clearTimeout(t);
  }, [user, storageKey, onboardingKey]);

  const dismiss = () => {
    if (storageKey) localStorage.setItem(storageKey, '1');
    setClosing(true);
    setTimeout(() => setVisible(false), 220);
  };

  if (!visible || !user) return null;

  const positionClass = {
    'top-right': 'top-20 right-6',
    'bottom-right': 'bottom-28 right-6',
    'bottom-left': 'bottom-28 left-6',
  }[position] || 'top-20 right-6';

  return (
    <div
      className={`fixed ${positionClass} z-[90] max-w-[340px] transition-all duration-200 ${
        closing ? 'opacity-0 translate-y-1' : 'opacity-100 translate-y-0'
      }`}
      data-testid={`feature-tip-${tipId}`}
    >
      <div className="flex items-end gap-2">
        {/* Mascot */}
        <img src={MASCOT} alt="" className="w-16 h-16 flex-shrink-0 drop-shadow-xl pointer-events-none select-none" />
        {/* Speech bubble */}
        <div className="relative bg-card rounded-2xl rounded-bl-sm border border-border shadow-2xl px-4 py-3 flex-1">
          <button
            onClick={dismiss}
            className="absolute top-2 right-2 w-5 h-5 rounded-full hover:bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
            data-testid={`feature-tip-close-${tipId}`}
            aria-label="Stäng tipset"
          >
            <X size={10} />
          </button>
          <p className="text-xs font-bold text-foreground pr-5" data-testid={`feature-tip-title-${tipId}`}>
            {title}
          </p>
          <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed" data-testid={`feature-tip-body-${tipId}`}>
            {body}
          </p>
          <button
            onClick={dismiss}
            className="text-[11px] font-semibold text-[#E8613A] hover:text-[#D4532F] mt-2.5"
            data-testid={`feature-tip-ok-${tipId}`}
          >
            Förstått ✓
          </button>
        </div>
      </div>
    </div>
  );
};

export default FeatureTip;
